import {
  AcademicManagementActivityStatusCount,
  AcademicManagementWorkloadStatusSum,
} from 'domain/models';

export const ACADEMIC_STATUS_COLORS = {
  scheduled: '#878a99',
  accomplished: '#299cdb',
  validated: '#0ab39c',
  pendingValidation: '#f7b84b',
  missed: '#adb5bd',
  justified: '#adb5bd',
  workloadAccomplished: '#878a99',
  workloadValidated: '#405189',
} as const;

type LegendItem = {
  label: string;
  value: string | number;
  color: string;
};

type MiniDonutProps = {
  segments: Array<{ value: number; color: string }>;
  size?: number;
  strokeWidth?: number;
};

const MiniDonut = ({
  segments,
  size = 56,
  strokeWidth = 8,
}: MiniDonutProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce(
    (sum, item) => sum + Math.max(item.value, 0),
    0,
  );
  const center = size / 2;

  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#e9ebec"
        strokeWidth={strokeWidth}
      />
      {total > 0 &&
        segments
          .filter((segment) => segment.value > 0)
          .map((segment, index) => {
            const length = (segment.value / total) * circumference;
            const circle = (
              <circle
                key={`${segment.color}-${index}`}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${center} ${center})`}
              />
            );
            offset += length;
            return circle;
          })}
    </svg>
  );
};

const StatusDot = ({ color }: { color: string }) => (
  <span
    className="rounded-circle d-inline-block flex-shrink-0"
    style={{
      width: 8,
      height: 8,
      backgroundColor: color,
    }}
  />
);

const StatusLegendList = ({ items }: { items: LegendItem[] }) => (
  <div className="d-flex flex-column gap-1">
    {items.map((item) => (
      <div key={item.label} className="d-flex align-items-center gap-2">
        <StatusDot color={item.color} />
        <span className="text-muted small">
          {item.value} {item.label}
        </span>
      </div>
    ))}
  </div>
);

const StatusPillList = ({ items }: { items: LegendItem[] }) => (
  <div className="d-flex flex-column gap-1">
    {items.map((item) => (
      <span
        key={item.label}
        className="badge rounded-pill fw-normal text-muted border bg-transparent"
        style={{ borderColor: '#ced4da' }}
      >
        {item.value} {item.label}
      </span>
    ))}
  </div>
);

export const formatWorkloadLabel = (value?: string) => {
  if (!value) return '00h00';

  const [hours = '0', minutes = '00'] = value.split(':');
  return `${hours.padStart(2, '0')}h${minutes.padStart(2, '0')}`;
};

export const ActivityStatusSummaryCell = ({
  status,
}: {
  status: AcademicManagementActivityStatusCount;
}) => {
  const primaryItems: LegendItem[] = [
    {
      label: 'Agendadas',
      value: status.scheduled,
      color: ACADEMIC_STATUS_COLORS.scheduled,
    },
    {
      label: 'Realizadas',
      value: status.accomplished,
      color: ACADEMIC_STATUS_COLORS.accomplished,
    },
    {
      label: 'Validadas',
      value: status.validated,
      color: ACADEMIC_STATUS_COLORS.validated,
    },
    {
      label: 'Pendentes de validação',
      value: status.pendingValidation,
      color: ACADEMIC_STATUS_COLORS.pendingValidation,
    },
  ];

  const pillItems: LegendItem[] = [
    {
      label: 'perdidas',
      value: status.missed,
      color: ACADEMIC_STATUS_COLORS.missed,
    },
    {
      label: 'justificadas',
      value: status.justified,
      color: ACADEMIC_STATUS_COLORS.justified,
    },
  ];

  return (
    <div className="d-flex align-items-center gap-3 py-2">
      <MiniDonut
        segments={[
          {
            value: status.scheduled,
            color: ACADEMIC_STATUS_COLORS.scheduled,
          },
          {
            value: status.accomplished,
            color: ACADEMIC_STATUS_COLORS.accomplished,
          },
          {
            value: status.validated,
            color: ACADEMIC_STATUS_COLORS.validated,
          },
          {
            value: status.pendingValidation,
            color: ACADEMIC_STATUS_COLORS.pendingValidation,
          },
        ]}
      />
      <StatusLegendList items={primaryItems} />
      <StatusPillList items={pillItems} />
    </div>
  );
};

export const WorkloadStatusSummaryCell = ({
  workload,
}: {
  workload: AcademicManagementWorkloadStatusSum;
}) => {
  const primaryItems: LegendItem[] = [
    {
      label: 'Realizadas',
      value: formatWorkloadLabel(workload.accomplished),
      color: ACADEMIC_STATUS_COLORS.workloadAccomplished,
    },
    {
      label: 'Validadas',
      value: formatWorkloadLabel(workload.validated),
      color: ACADEMIC_STATUS_COLORS.workloadValidated,
    },
  ];

  const pillItems: LegendItem[] = [
    {
      label: 'perdidas',
      value: formatWorkloadLabel(workload.missed),
      color: ACADEMIC_STATUS_COLORS.missed,
    },
    {
      label: 'justificadas',
      value: formatWorkloadLabel(workload.justified),
      color: ACADEMIC_STATUS_COLORS.justified,
    },
  ];

  return (
    <div className="d-flex align-items-center gap-3 py-2">
      <StatusLegendList items={primaryItems} />
      <StatusPillList items={pillItems} />
    </div>
  );
};
